/**
 * Employment verification service
 * Handles employment verification workflow
 */

import axios from "axios";
import api from "../api";

export interface VerificationCompany {
  slug: string;
  company_name: string;
}

export interface EmploymentVerificationStatus {
  /** "review": the code was confirmed and staff are checking the company. */
  status: "pending" | "verified" | "review";
  verified_at?: string;
  company?: VerificationCompany;
}

export interface VerificationSubmitData {
  work_email: string;
  /** A directory company the member picked. */
  company_slug?: string;
  /** A company typed by the member; used when no slug is picked. */
  company_name?: string;
  handle_id?: string;
}

export interface WorkEmailLookup {
  /** Gmail, Yahoo, Outlook and the like: can't confirm an employer. */
  personal: boolean;
  /** The company this email domain is already linked to, if any. */
  company: VerificationCompany | null;
}

/** A refused submit, with the backend's code so the page can mark the right field. */
export class VerificationSubmitError extends Error {
  constructor(public code: string, message: string) {
    super(message);
    this.name = "VerificationSubmitError";
  }
}

export const PERSONAL_EMAIL_MESSAGE =
  "Use your company email. Gmail, Yahoo, Outlook, Hotmail, temporary inboxes and other personal addresses can't confirm where you work.";

export interface VerificationConfirmData {
  verification_code: string;
  handle_id?: string;
  attempt_id?: string;
}

export interface VerificationResendData {
  work_email: string;
  handle_id?: string;
  attempt_id?: string;
}

/**
 * Check if user is verified
 */
export async function isUserVerified() {
  try {
    const response = await api.get("/api/account/verified/");
    return response.data as { verified: boolean };
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.detail || error.response?.data?.error || error.message);
    }
    throw new Error("An unknown error occured!");
  }
}

/**
 * Get employment verification status
 */
export async function getEmploymentVerificationStatus() {
  try {
    const response = await api.get("/api/account/employment-verification/status/");
    return response.data as EmploymentVerificationStatus;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      const data = error.response?.data as { detail?: string } | undefined;
      throw new Error(data?.detail || error.message);
    }
    throw new Error("An unknown error occured!");
  }
}

/**
 * Submit employment verification request
 */
export async function submitEmploymentVerification(data: VerificationSubmitData) {
  try {
    const response = await api.post("/api/account/employment-verification/submit/", data);
    return response.data as {
      status: string;
      attempt_id?: string;
      expires_at?: string;
      company?: VerificationCompany;
      /** Staff will check the company once the code is confirmed. */
      needs_review?: boolean;
    };
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      const body = error.response?.data as { error?: string; detail?: string } | undefined;

      const messageMap: Record<string, string> = {
        invalid_work_email: "Enter a valid work email.",
        personal_email: PERSONAL_EMAIL_MESSAGE,
        company_name_required: "Choose your company, or type its full name.",
        company_name_too_long: "That company name is too long. Use 100 characters or fewer.",
        unknown_company: "We couldn't find that company. Choose it again from the list.",
        email_undeliverable:
          "We couldn't deliver to that address. Check for a typo and try again.",
        email_send_failed:
          "We couldn't send the email right now. Please try again in a moment.",
      };

      const code = body?.error;
      if (code && messageMap[code]) {
        throw new VerificationSubmitError(code, messageMap[code]);
      }
      if (error.response?.status === 429) {
        throw new VerificationSubmitError("throttled", "Too many tries. Wait a little and try again.");
      }

      throw new Error(body?.detail || code || error.message);
    }
    throw new Error("An unknown error occured!");
  }
}

/**
 * What Tappanon already knows about a work-email domain: whether it is a
 * personal mailbox, and which company it is linked to. Sends the domain only,
 * never the address.
 */
export async function lookupWorkEmailDomain(domain: string, signal?: AbortSignal) {
  const response = await api.get("/api/account/employment-verification/lookup/", {
    params: { domain },
    signal,
  });
  return response.data as WorkEmailLookup;
}

/** Whether the one-time "company verification now works" pop-up is due. */
export async function getVerificationPrompt() {
  const response = await api.get("/api/account/employment-verification/prompt/");
  return response.data as { show: boolean };
}

/** Record that the pop-up was shown, so it never shows again, on any device. */
export async function markVerificationPromptSeen() {
  await api.post("/api/account/employment-verification/prompt/");
}

/**
 * Confirm employment verification with code
 */
export async function confirmEmploymentVerification(data: VerificationConfirmData) {
  try {
    const response = await api.post("/api/account/employment-verification/confirm/", data);
    return response.data as { status: string; verified_at?: string; company?: VerificationCompany };
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      const body = error.response?.data as { error?: string; detail?: string } | undefined;

      const messageMap: Record<string, string> = {
        expired_code: "expired_code",
        invalid_code: "invalid_code",
        personal_email: PERSONAL_EMAIL_MESSAGE,
      };

      const code = body?.error;
      if (code && messageMap[code]) {
        throw new Error(messageMap[code]);
      }

      throw new Error(body?.detail || code || error.message);
    }
    throw new Error("An unknown error occured!");
  }
}

/**
 * Resend employment verification code
 */
export async function resendEmploymentVerification(data: VerificationResendData) {
  try {
    const response = await api.post("/api/account/employment-verification/resend/", data);

    const headers = response.headers as Record<string, string | undefined>;
    const retryAfterHeader = headers?.["retry-after"] ?? headers?.["Retry-After"];
    const retryAfter = retryAfterHeader ? Number.parseInt(retryAfterHeader, 10) : undefined;

    return {
      ...response.data,
      retry_after: typeof retryAfter === "number" && !Number.isNaN(retryAfter) ? retryAfter : undefined,
    } as { status: string; expires_at?: string; cooldown?: number; retry_after?: number };
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      const body = error.response?.data as { error?: string; detail?: string } | undefined;

      const messageMap: Record<string, string> = {
        work_email_required: "Provide the work email used for verification.",
        no_pending_verification: "No pending verification found to resend.",
        work_email_mismatch: "Codes only go to the address you started with. Start over to use a different one.",
        too_many_resends:
          "You've requested too many codes. Start over to send to a new address.",
        email_undeliverable:
          "We couldn't deliver to that address — check for a typo and try again.",
        email_send_failed:
          "We couldn't send the email right now. Please try again in a moment.",
      };

      const code = body?.error;
      if (code && messageMap[code]) {
        throw new Error(messageMap[code]);
      }

      throw new Error(body?.detail || code || error.message);
    }
    throw new Error("An unknown error occured!");
  }
}

/**
 * Request email verification
 */
export async function requestVerificationEmail(data: { work_email: string }) {
  try {
    const response = await api.post("/api/account/request/", data);
    return response.data as { detail: string };
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.detail || error.message);
    }
    throw new Error("An unknown error occured!");
  }
}

/**
 * Verify email with token
 */
export async function verifyEmail(data: { token: string | null }) {
  try {
    if (!data.token) throw new Error("No verification token provided.");

    const response = await api.get(`/api/account/confirm/`, {
      params: { t: data.token },
    });

    return response.data as { detail: string };
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.detail || error.message);
    }
    throw new Error("An unknown error occured!");
  }
}
